import { useTranslation } from '@axo/shared/i18n';
import {
  IInputInfoBoxUI as IInputInfoBox,
  InfoType,
} from '@axo/shared/ui/input/lib/InputInfoBox/InputInfoBox.types';
import { useEffect, useState } from 'react';
import {
  FieldError,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { IValidationFunctions } from '../lib/types';

interface IValidationResult {
  infoBox?: IInputInfoBox;
  isValidating?: boolean;
}
interface Props extends IValidationFunctions {
  name: string;
  hasFocus?: boolean;
  formControllerError?: FieldError;
}

export const useValidationUI = ({
  name,
  warningValidation,
  tooltipValidation,
  successValidation,
  hasFocus,
  formControllerError,
}: Props): IValidationResult => {
  const { t } = useTranslation();

  const { touchedFields, errors } = useFormState();
  const { getValues, setFocus } = useFormContext();
  const [dismissed, setDismissed] = useState(false);

  const value = useWatch({ name });

  useEffect(() => {
    setDismissed(false);
  }, [value]);

  const isTouched = touchedFields[name];
  const error = errors[name];
  const onDismiss = () => setDismissed(true);
  const isValidating =
    warningValidation?.isValidating ||
    tooltipValidation?.isValidating ||
    successValidation?.isValidating;

  if (isValidating) {
    return { isValidating };
  }

  if (error) {
    const message =
      typeof error.message === 'string' ? t(error.message) : undefined;

    return {
      infoBox: {
        message,
        type: InfoType.ERROR,
      },
    };
  }

  if (formControllerError) {
    const message =
      typeof formControllerError.message === 'string'
        ? t(formControllerError.message)
        : undefined;

    return {
      infoBox: {
        message,
        type: InfoType.ERROR,
      },
    };
  }

  if (warningValidation && !dismissed && !hasFocus && isTouched) {
    const prompt = {
      accept:
        warningValidation.prompt?.accept && t(warningValidation.prompt?.accept),
      reject:
        warningValidation.prompt?.reject && t(warningValidation.prompt?.reject),
    };

    const message =
      typeof warningValidation.message === 'string'
        ? warningValidation.message
        : warningValidation.message(value, getValues());

    if (message) {
      return {
        infoBox: {
          onAccept: () => {
            warningValidation.onAccept?.();
            onDismiss();
          },
          onReject: () => {
            warningValidation.onReject?.();
            setFocus(name);
            onDismiss();
          },
          message: t(message),
          prompt,
          type: InfoType.WARNING,
        },
      };
    }
  }

  if (tooltipValidation) {
    const message =
      typeof tooltipValidation.message === 'string'
        ? tooltipValidation.message
        : tooltipValidation.message(value);

    if (message) {
      return {
        infoBox: {
          message: t(message),
          type: InfoType.TOOLTIP,
        },
      };
    }
  }

  if (successValidation && !hasFocus) {
    const message =
      typeof successValidation.message === 'string'
        ? successValidation.message
        : successValidation.message(value);

    if (message) {
      return {
        infoBox: {
          message: t(message),
          type: InfoType.SUCCESS,
        },
      };
    }
  }

  return {};
};
